import { useCallback, useEffect, useMemo, useState } from 'react';
import * as Sentry from '@sentry/browser';
import { useAuth0 } from '@auth0/auth0-react';
import {
  Alert,
  Box,
  Stack,
  Table,
  TableBody,
  TableContainer,
  TableRow,
  Typography,
} from '@mui/material';
import { format } from 'date-fns';
import { getHeadCells, loginIcons } from './data';
import { UsersTableHead } from '../shared/UsersTableHead';
import { StyledTableCell, TablePaginationContainer } from '../styles';
import {
  AllUsersForUserManagementQuery,
  useAllUsersForUserManagementQuery,
  useBlockOrUnblockUserMutation,
  useResetPasswordMutation,
  User as UserType,
  User,
  UserStatusEnum,
} from '@/__generated__/graphql';
import { abyssColors } from '@/theme/colors';
import { Order } from '@/types';
import { ConfirmationDialog } from '../Dialogs/ConfirmationDialog';
import { useAtom, useSetAtom } from 'jotai';
import { UserActions } from './UserActions';
import { useRouter } from '@/hooks';
import { userManagementSearchPlaceholder, userManagementSearchTerm } from '../state';
import { useDebounce } from '@/hooks/useDebounce';
import { RolesCell } from '../shared/RolesCell';
import { TableSkeleton } from '../shared/TableSkeleton';
import { UserAvatarCell } from '../shared/UserAvatarCell';
import { TableDataNotFound } from '../shared/TableDataNotFound';
import { useSnackBarMessage } from '@/utils/useSnackBarMessage';
import { TablePagination } from '../../shared/TablePagination';
import InfoIcon from '@mui/icons-material/Info';
import { defaultPaginationSize } from '@/constants';

const headCells = getHeadCells();
const USERNAME_PASSWORD_CONNECTION = 'Username-Password-Authentication';

export const AllUsersPanel = () => {
  const currentUser = useAuth0().user;

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [order, setOrder] = useState<Order>('desc');
  const [orderBy, setOrderBy] = useState<keyof UserType>('lastLogin');
  const [userId, setUserId] = useState<string | undefined>(undefined);
  const { showSnackBar } = useSnackBarMessage({ variant: 'filled' });
  const [toggleConfirmationDialog, setToggleConfirmationDialog] = useState(false);
  const [allUsers, setAllUsers] = useState<AllUsersForUserManagementQuery['allUsers']>([]);
  const [resetPasswordLoading, setResetPasswordLoading] = useState<{
    [key: string]: boolean;
  }>({});
  const router = useRouter();

  const setSearchPlaceholder = useSetAtom(userManagementSearchPlaceholder);
  const [searchValue, setSearchTerm] = useAtom(userManagementSearchTerm);
  const searchTerm = useDebounce(searchValue, 700);

  useEffect(() => {
    setSearchPlaceholder('Search Users');
    return () => {
      setSearchTerm('');
    };
  }, [setSearchPlaceholder, setSearchTerm]);

  useEffect(() => {
    setPage(0);
    setRowsPerPage(10);
    setOrder('desc');
    setOrderBy('lastLogin');
  }, [searchTerm]);

  const [blockOrUnblockUser] = useBlockOrUnblockUserMutation();

  const { data, loading } = useAllUsersForUserManagementQuery({
    variables: {
      input: { q: searchTerm },
      pageInfo: {
        skip: page * rowsPerPage,
        limit: rowsPerPage,
        sortBy: orderBy,
        sortDir: order === 'asc' ? 1 : -1,
      },
      usersCountInput: { q: searchTerm },
    },
  });

  const [resetPassword] = useResetPasswordMutation();

  const handleChangePage = (newPage: number) => setPage(newPage);
  const handleChangeRowsPerPage = (updatedRowsPerPage: number) => {
    setRowsPerPage(updatedRowsPerPage);
    setPage(0);
  };

  const handleRequestSort = (headerId: keyof UserType) => {
    const isAsc = orderBy === headerId && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(headerId);
  };

  const handleUserClick = (id: string) => {
    router.push({
      pathname: `/userManagement/user/${id}`,
    });
  };

  const usersCount = useMemo(() => {
    if (data?.allUsers) setAllUsers(data?.allUsers);
    return data?.usersCount?.count ?? 0;
  }, [data?.allUsers, data?.usersCount]);

  const handleOpenConfirmationDialog = (id: string) => {
    setUserId(id);
    setToggleConfirmationDialog(true);
  };

  const handleResetPassword = ({
    id,
    email,
    connection,
    loginCount,
  }: AllUsersForUserManagementQuery['allUsers'][0]) => {
    setResetPasswordLoading((previousState) => ({
      ...previousState,
      [id]: true,
    }));
    resetPassword({
      variables: {
        input: {
          connection: connection ?? USERNAME_PASSWORD_CONNECTION,
          clientId: import.meta.env.VITE_APP_AUTH0_CLIENT_ID,
          email,
        },
      },
    })
      .then(() => {
        const message =
          loginCount && loginCount > 0
            ? 'Password reset email sent successfully'
            : 'Invitation resent successfully';
        showSnackBar(message, 'success');
      })
      .catch((error) => {
        const message =
          loginCount && loginCount > 0 ? 'Failed to reset password' : 'Failed to resend invite';
        Sentry.captureMessage(`${message}: ${error.message}`, {
          user: { sub: currentUser },
          extra: {
            connection: connection ?? USERNAME_PASSWORD_CONNECTION,
            clientId: import.meta.env.VITE_APP_AUTH0_CLIENT_ID,
            email: email,
          },
        });
        showSnackBar(`${message}: ${error.message}`, 'error');
      })
      .finally(() => {
        setResetPasswordLoading((previousState) => ({
          ...previousState,
          [id]: false,
        }));
      });
  };

  const handleBlockOrUnblockUser = () => {
    if (!userId) return;
    blockOrUnblockUser({ variables: { userId } })
      .then(({ data: userData }) => {
        const updatedUser = userData?.blockOrUnblockUser;
        if (!updatedUser) {
          Sentry.captureMessage('Failed to block/unblock user', {
            user: { sub: currentUser },
            extra: {
              data: userData,
            },
          });
          showSnackBar('Something went wrong', 'error');
          return;
        }

        showSnackBar('This user has been blocked', 'success');

        const { Pending, Failed } = UserStatusEnum;

        setAllUsers((previousUsers) =>
          previousUsers.reduce((accumulator, user) => {
            if (user.id === updatedUser.id) {
              if (user.status === Pending || user.status === Failed) return accumulator;
              accumulator.push({ ...user, status: updatedUser.status });
            } else {
              accumulator.push(user);
            }
            return accumulator;
          }, [] as User[])
        );
      })
      .catch((error) => {
        showSnackBar(error.message, 'error');
      });
    setToggleConfirmationDialog(false);
  };

  const lastLoginText = useCallback(
    (inviteStatusPending: boolean, login: string | undefined | null) => {
      if (inviteStatusPending) {
        return (
          <Alert
            icon={<InfoIcon fontSize="inherit" />}
            severity="info"
            sx={{
              mr: 0,
              p: 0,
              backgroundColor: 'transparent',
              color: 'black',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            Invite Sent
          </Alert>
        );
      }

      return login ? format(Number(login), 'dd-MM-yyyy') : 'N/A';
    },
    []
  );

  const isBlocked = allUsers.find((user) => user.id === userId)?.status === UserStatusEnum.Blocked;
  const confirmationDialogMessage = isBlocked
    ? 'Are you sure you want to take this action?'
    : 'Are you sure you want to proceed? This user will lose all access. To restore access later, you will need to contact support.';

  return (
    <Box
      sx={{
        height: '100%',
        overflowY: 'auto',
        msOverflowStyle: 'none',
        scrollbarWidth: 'none',
        '&::-webkit-scrollbar': { display: 'none' },
      }}
    >
      <Box p={5} pl={7}>
        <Typography fontSize="2.8rem" fontWeight={700} color={abyssColors.primary[500]}>
          All users
        </Typography>
        <Typography fontSize="1.6rem" fontWeight={400} color={abyssColors.primary[400]}>
          All Abyss Users
        </Typography>
        <TableContainer sx={{ mt: 3, overflowY: 'auto' }}>
          <Table sx={{ minWidth: 750 }} aria-labelledby="tableTitle">
            {!loading && allUsers.length > 0 && (
              <UsersTableHead
                order={order}
                orderBy={orderBy}
                headCells={headCells}
                onRequestSort={handleRequestSort}
              />
            )}
            <TableBody>
              {loading && <TableSkeleton noOfSimpleColumns={4} />}
              {!loading &&
                allUsers.map((user) => {
                  const {
                    id,
                    name,
                    email,
                    picture,
                    roleIds,
                    lastLogin,
                    connection,
                    loginCount,
                    status,
                  } = user;
                  const isInvitationPending = status === UserStatusEnum.Pending;
                  return (
                    <TableRow
                      key={id}
                      hover
                      sx={{ fontSize: '1.4rem' }}
                      tabIndex={-1}
                      data-testid="user-row"
                      onClick={() => handleUserClick(id)}
                    >
                      <StyledTableCell sx={{ width: '300px' }}>
                        <UserAvatarCell name={name} picture={picture} />
                      </StyledTableCell>
                      <StyledTableCell sx={{ width: '120px' }}>{email}</StyledTableCell>
                      <StyledTableCell sx={{ width: '20%' }}>
                        <RolesCell roleIds={roleIds} />
                      </StyledTableCell>
                      <StyledTableCell sx={{ width: '130px' }}>
                        {lastLoginText(isInvitationPending, lastLogin)}
                      </StyledTableCell>
                      <StyledTableCell
                        sx={{
                          width: '100px',
                          paddingLeft: '32px',
                          height: 'fit-content',
                        }}
                      >
                        <Stack justifyContent="center" width="fit-content">
                          {connection ? loginIcons(connection) : 'N/A'}
                        </Stack>
                      </StyledTableCell>
                      <StyledTableCell sx={{ width: '110px' }}>{status ?? 'N/A'}</StyledTableCell>
                      <StyledTableCell sx={{ width: '110px' }}>
                        {isInvitationPending ? 'N/A' : loginCount}
                      </StyledTableCell>
                      <StyledTableCell
                        sx={{ width: '70px' }}
                        onClick={(event) => event.stopPropagation()}
                      >
                        <UserActions
                          user={user}
                          loading={resetPasswordLoading[id]}
                          isInvitationPending={isInvitationPending}
                          handleOpenConfirmationDialog={handleOpenConfirmationDialog}
                          handleResetPassword={handleResetPassword}
                        />
                      </StyledTableCell>
                    </TableRow>
                  );
                })}
            </TableBody>
          </Table>
        </TableContainer>
        {!loading && allUsers.length === 0 && <TableDataNotFound message="No Users Found" />}
        {!loading && allUsers.length > 0 && (
          <TablePaginationContainer data-testid="pagination-component">
            <TablePagination
              count={usersCount}
              page={page}
              rowsPerPage={rowsPerPage}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              rowsPerPageOptions={[...defaultPaginationSize, { label: 'All', value: usersCount }]}
            />
          </TablePaginationContainer>
        )}
        {toggleConfirmationDialog && (
          <ConfirmationDialog
            title={isBlocked ? 'Unblock User' : 'Blocking the user'}
            message={confirmationDialogMessage}
            buttonText={isBlocked ? 'Unblock' : 'Block'}
            handleRemoveUserFromGroup={handleBlockOrUnblockUser}
            onClose={() => setToggleConfirmationDialog(false)}
            open={toggleConfirmationDialog}
          />
        )}
      </Box>
    </Box>
  );
};
